import React, { useState, useEffect } from 'react';
import { PageHeader, SectionTitle, Card, Badge } from '../design-system';
import { useGetRateCard, usePutRateCard } from '../api/casecost';
import styles from './page-rate-card-editor.module.css';

const TIER_NAMES = ['basic', 'standard', 'advanced'];

// ---- Rate-card editor -----------------------------------------------------

function parseCard(card) {
  const models = card?.models || {};
  const mtm = card?.model_tier_map || {};
  const modelRows = Object.keys(models).map((id) => ({
    id,
    input: String(models[id]?.in_per_mtok ?? ''),
    output: String(models[id]?.out_per_mtok ?? ''),
    tier: mtm[id] || '',
  }));
  const tiers = card?.tiers || {};
  const tierRows = TIER_NAMES.map((name) => ({
    name,
    input: String(tiers[name]?.in_per_mtok ?? ''),
    output: String(tiers[name]?.out_per_mtok ?? ''),
  }));
  const m = card?.multipliers || {};
  return {
    modelRows,
    tierRows,
    multipliers: {
      cache_read: String(m.cache_read ?? ''),
      cache_write: String(m.cache_write ?? ''),
      batch: String(m.batch ?? ''),
    },
  };
}

function buildCard(modelRows, tierRows, multipliers) {
  const models = {};
  const model_tier_map = {};
  modelRows.forEach((r) => {
    const id = r.id.trim();
    if (!id) return;
    models[id] = { in_per_mtok: Number(r.input) || 0, out_per_mtok: Number(r.output) || 0 };
    if (r.tier) model_tier_map[id] = r.tier;
  });
  const tiers = {};
  tierRows.forEach((t) => {
    tiers[t.name] = { in_per_mtok: Number(t.input) || 0, out_per_mtok: Number(t.output) || 0 };
  });
  return {
    models,
    tiers,
    model_tier_map,
    multipliers: {
      cache_read: Number(multipliers.cache_read) || 0,
      cache_write: Number(multipliers.cache_write) || 0,
      batch: Number(multipliers.batch) || 0,
    },
  };
}

function RateInput({ value, onChange, ariaLabel, text = false }) {
  return (
    <input
      className={styles.rcInput}
      type={text ? 'text' : 'number'}
      min={text ? undefined : '0'}
      step={text ? undefined : '0.01'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    />
  );
}

function RateCardEditor() {
  const { data, isLoading } = useGetRateCard();
  const putRateCard = usePutRateCard();

  const [modelRows, setModelRows] = useState([]);
  const [tierRows, setTierRows] = useState(TIER_NAMES.map((name) => ({ name, input: '', output: '' })));
  const [multipliers, setMultipliers] = useState({ cache_read: '', cache_write: '', batch: '' });
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (data?.card) {
      const parsed = parseCard(data.card);
      setModelRows(parsed.modelRows);
      setTierRows(parsed.tierRows);
      setMultipliers(parsed.multipliers);
    }
  }, [data]);

  const updateModel = (idx, field, value) =>
    setModelRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const removeModel = (idx) => setModelRows((rows) => rows.filter((_, i) => i !== idx));
  const addModel = () =>
    setModelRows((rows) => [...rows, { id: '', input: '', output: '', tier: '' }]);
  const updateTier = (idx, field, value) =>
    setTierRows((rows) => rows.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));

  const onPublish = async () => {
    setStatus('');
    try {
      const res = await putRateCard(buildCard(modelRows, tierRows, multipliers));
      setStatus(`Published version ${res?.version ?? ''} — now live for all cases.`);
      setConfirming(false);
    } catch (e) {
      setStatus('Publish failed.');
      setConfirming(false);
    }
  };

  return (
    <Card padding="md">
      <SectionTitle actions={data?.version != null ? <Badge variant="info">v{data.version}</Badge> : null}>
        Current rate card
      </SectionTitle>

      <div className={styles.rcBanner} role="note">
        <Badge variant="warning">System-wide</Badge>
        <span className={styles.rcBannerText}>
          This is a single global rate card — it prices AI cost for <strong>every case</strong>.
          Publishing creates a new effective-dated version that applies system-wide going forward;
          already-frozen historical costs are unaffected.
        </span>
      </div>

      {isLoading ? (
        <p className={styles.empty}>Loading…</p>
      ) : (
        <>
          <div className={styles.rcSection}>
            <div className={styles.rcSubtitle}>Models ($ per million tokens)</div>
            <table className={styles.rcTable}>
              <thead>
                <tr>
                  <th className={styles.rcTh}>Model ID</th>
                  <th className={styles.rcTh}>Input</th>
                  <th className={styles.rcTh}>Output</th>
                  <th className={styles.rcTh}>Tier</th>
                  <th className={styles.rcTh} aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {modelRows.map((row, idx) => (
                  <tr key={idx}>
                    <td className={styles.rcCell}>
                      <RateInput text value={row.id} onChange={(v) => updateModel(idx, 'id', v)} ariaLabel="Model id" />
                    </td>
                    <td className={styles.rcCell}>
                      <RateInput value={row.input} onChange={(v) => updateModel(idx, 'input', v)} ariaLabel="Input rate per million" />
                    </td>
                    <td className={styles.rcCell}>
                      <RateInput value={row.output} onChange={(v) => updateModel(idx, 'output', v)} ariaLabel="Output rate per million" />
                    </td>
                    <td className={styles.rcCell}>
                      <select
                        className={styles.rcSelect}
                        value={row.tier}
                        onChange={(e) => updateModel(idx, 'tier', e.target.value)}
                        aria-label="Capability tier"
                      >
                        <option value="">—</option>
                        {TIER_NAMES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td className={styles.rcCell}>
                      <button
                        type="button"
                        className={styles.rcRemoveBtn}
                        onClick={() => removeModel(idx)}
                        aria-label={`Remove ${row.id || 'model'}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className={styles.rcAddBtn} onClick={addModel}>
              + Add model
            </button>
          </div>

          <div className={styles.rcSection}>
            <div className={styles.rcSubtitle}>Capability tiers (fallback when a model isn’t listed)</div>
            <table className={styles.rcTable}>
              <thead>
                <tr>
                  <th className={styles.rcTh}>Tier</th>
                  <th className={styles.rcTh}>Input</th>
                  <th className={styles.rcTh}>Output</th>
                </tr>
              </thead>
              <tbody>
                {tierRows.map((t, idx) => (
                  <tr key={t.name}>
                    <td className={styles.rcCell}>{t.name}</td>
                    <td className={styles.rcCell}>
                      <RateInput value={t.input} onChange={(v) => updateTier(idx, 'input', v)} ariaLabel={`${t.name} input rate`} />
                    </td>
                    <td className={styles.rcCell}>
                      <RateInput value={t.output} onChange={(v) => updateTier(idx, 'output', v)} ariaLabel={`${t.name} output rate`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.rcSection}>
            <div className={styles.rcSubtitle}>Multipliers</div>
            <div className={styles.rcMultipliers}>
              <label className={styles.rcMultLabel}>
                Cache read
                <RateInput value={multipliers.cache_read} onChange={(v) => setMultipliers((m) => ({ ...m, cache_read: v }))} ariaLabel="Cache read multiplier" />
              </label>
              <label className={styles.rcMultLabel}>
                Cache write
                <RateInput value={multipliers.cache_write} onChange={(v) => setMultipliers((m) => ({ ...m, cache_write: v }))} ariaLabel="Cache write multiplier" />
              </label>
              <label className={styles.rcMultLabel}>
                Batch
                <RateInput value={multipliers.batch} onChange={(v) => setMultipliers((m) => ({ ...m, batch: v }))} ariaLabel="Batch multiplier" />
              </label>
            </div>
          </div>

          <div className={styles.rcActions}>
            {!confirming ? (
              <button type="button" className={styles.rcPublishBtn} onClick={() => { setStatus(''); setConfirming(true); }}>
                Publish new version
              </button>
            ) : (
              <div className={styles.rcConfirm} role="alertdialog" aria-label="Confirm system-wide rate change">
                <span className={styles.rcConfirmText}>
                  This publishes a new rate card that changes AI cost pricing for <strong>all cases system-wide</strong>. Continue?
                </span>
                <button type="button" className={styles.rcConfirmBtn} onClick={onPublish}>
                  Publish to all cases
                </button>
                <button type="button" className={styles.rcCancelBtn} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            )}
            {status && <span className={styles.rcStatus}>{status}</span>}
          </div>
        </>
      )}
    </Card>
  );
}

// ---- Page -----------------------------------------------------------------

export default function RateCardEditorPage() {
  return (
    <div className={styles.page}>
      <PageHeader
        title="AI Rate Card"
        subtitle="System-wide AI pricing used to compute internal cost — not customer pricing. Super-admins only."
      />
      <section className={styles.section}>
        <RateCardEditor />
      </section>
    </div>
  );
}
